import { useState } from "react";
import { useLang } from "../../context/LanguageContext";

/* ─────────────────────────────────────────────
   Generic A4 Document SVG Illustrations
───────────────────────────────────────────── */

/** Shared document body — lines of text + logo area */
function DocBody() {
  return (
    <>
      {/* Paper */}
      <rect x="44" y="10" width="152" height="196" rx="4" fill="#f8f9fb" stroke="#d1d5db" strokeWidth="1" />
      {/* Header bar */}
      <rect x="44" y="10" width="152" height="28" rx="4" fill="#e8edf3" />
      <rect x="44" y="28" width="152" height="10" fill="#e8edf3" />
      {/* Logo square */}
      <rect x="52" y="16" width="18" height="18" rx="2" fill="#94a3b8" />
      <rect x="55" y="19" width="12" height="2" rx="1" fill="#fff" />
      <rect x="55" y="23" width="8" height="2" rx="1" fill="#fff" />
      {/* Header title lines */}
      <rect x="76" y="18" width="60" height="5" rx="2" fill="#64748b" />
      <rect x="76" y="26" width="40" height="3" rx="1.5" fill="#94a3b8" />
      {/* Divider */}
      <line x1="52" y1="42" x2="188" y2="42" stroke="#e2e8f0" strokeWidth="1" />
      {/* Text rows */}
      <rect x="52" y="50" width="100" height="4" rx="2" fill="#cbd5e1" />
      <rect x="52" y="58" width="136" height="4" rx="2" fill="#e2e8f0" />
      <rect x="52" y="66" width="120" height="4" rx="2" fill="#e2e8f0" />
      <rect x="52" y="74" width="136" height="4" rx="2" fill="#e2e8f0" />
      <rect x="52" y="82" width="90" height="4" rx="2" fill="#e2e8f0" />
      {/* Section divider */}
      <line x1="52" y1="94" x2="188" y2="94" stroke="#e2e8f0" strokeWidth="0.8" />
      {/* More text rows */}
      <rect x="52" y="102" width="80" height="4" rx="2" fill="#cbd5e1" />
      <rect x="52" y="110" width="136" height="4" rx="2" fill="#e2e8f0" />
      <rect x="52" y="118" width="110" height="4" rx="2" fill="#e2e8f0" />
      <rect x="52" y="126" width="136" height="4" rx="2" fill="#e2e8f0" />
      <rect x="52" y="134" width="100" height="4" rx="2" fill="#e2e8f0" />
      {/* Stamp circle */}
      <circle cx="168" cy="168" r="18" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="3 2" />
      <circle cx="168" cy="168" r="12" fill="none" stroke="#94a3b8" strokeWidth="0.8" />
      <rect x="160" y="166" width="16" height="3" rx="1" fill="#94a3b8" />
      {/* Signature line */}
      <line x1="52" y1="178" x2="130" y2="178" stroke="#cbd5e1" strokeWidth="1" />
      <rect x="52" y="182" width="50" height="3" rx="1.5" fill="#e2e8f0" />
    </>
  );
}

/** Good — clean, flat, well-lit */
function GoodDocSVG() {
  return (
    <svg viewBox="0 0 240 216" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="240" height="216" fill="#f0f4f8" />
      <DocBody />
    </svg>
  );
}

/** Bad 1 — shadow across document */
function ShadowDocSVG() {
  return (
    <svg viewBox="0 0 240 216" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="240" height="216" fill="#f0f4f8" />
      <DocBody />
      {/* Dark diagonal shadow overlay */}
      <polygon
        points="44,80 196,10 196,100 44,180"
        fill="rgba(15,23,42,0.55)"
      />
    </svg>
  );
}

/** Bad 2 — tilted + corner cut off */
function AngledDocSVG() {
  return (
    <svg viewBox="0 0 240 216" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="240" height="216" fill="#c8b89a" />
      <g transform="rotate(-14, 120, 108) translate(-10, -8)">
        <DocBody />
      </g>
      {/* Mask to simulate cropped corner */}
      <polygon points="0,0 240,0 240,60 160,0" fill="#c8b89a" />
      <polygon points="0,180 0,216 80,216" fill="#c8b89a" />
    </svg>
  );
}

/** Bad 3 — too dark */
function DarkDocSVG() {
  return (
    <svg viewBox="0 0 240 216" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="240" height="216" fill="#1e2533" />
      {/* Dark version of document */}
      <rect x="44" y="10" width="152" height="196" rx="4" fill="#2d3748" stroke="#4a5568" strokeWidth="1" />
      <rect x="44" y="10" width="152" height="28" rx="4" fill="#374151" />
      <rect x="44" y="28" width="152" height="10" fill="#374151" />
      <rect x="52" y="16" width="18" height="18" rx="2" fill="#4b5563" />
      <rect x="55" y="19" width="12" height="2" rx="1" fill="#6b7280" />
      <rect x="55" y="23" width="8" height="2" rx="1" fill="#6b7280" />
      <rect x="76" y="18" width="60" height="5" rx="2" fill="#4b5563" />
      <rect x="76" y="26" width="40" height="3" rx="1.5" fill="#374151" />
      <line x1="52" y1="42" x2="188" y2="42" stroke="#374151" strokeWidth="1" />
      <rect x="52" y="50" width="100" height="4" rx="2" fill="#374151" />
      <rect x="52" y="58" width="136" height="4" rx="2" fill="#374151" />
      <rect x="52" y="66" width="120" height="4" rx="2" fill="#374151" />
      <rect x="52" y="74" width="136" height="4" rx="2" fill="#374151" />
      <rect x="52" y="82" width="90" height="4" rx="2" fill="#374151" />
      <line x1="52" y1="94" x2="188" y2="94" stroke="#374151" strokeWidth="0.8" />
      <rect x="52" y="102" width="80" height="4" rx="2" fill="#374151" />
      <rect x="52" y="110" width="136" height="4" rx="2" fill="#374151" />
      <rect x="52" y="118" width="110" height="4" rx="2" fill="#374151" />
      <rect x="52" y="126" width="136" height="4" rx="2" fill="#374151" />
      <rect x="52" y="134" width="100" height="4" rx="2" fill="#374151" />
      <circle cx="168" cy="168" r="18" fill="none" stroke="#4b5563" strokeWidth="1.5" strokeDasharray="3 2" />
      <line x1="52" y1="178" x2="130" y2="178" stroke="#374151" strokeWidth="1" />
      <rect x="52" y="182" width="50" height="3" rx="1.5" fill="#374151" />
    </svg>
  );
}

type BadSlideId = "bad1" | "bad2" | "bad3";
type ExpandedId = "good" | BadSlideId;

function FullscreenDocSVG({ id }: { id: ExpandedId }) {
  if (id === "good") return <GoodDocSVG />;
  if (id === "bad1") return <ShadowDocSVG />;
  if (id === "bad2") return <AngledDocSVG />;
  return <DarkDocSVG />;
}

/* ─────────────────────────────────────────────
   DocPhotoGuideModal — exported component
───────────────────────────────────────────── */

interface DocPhotoGuideModalProps {
  open: boolean;
  onClose: () => void;
}

export default function DocPhotoGuideModal({ open, onClose }: DocPhotoGuideModalProps) {
  const { t } = useLang();
  const [expandedImage, setExpandedImage] = useState<ExpandedId | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  if (!open) return null;

  const badSlides: { id: BadSlideId; captionKey: string }[] = [
    { id: "bad1", captionKey: "docPhotoGuide.bad1" },
    { id: "bad2", captionKey: "docPhotoGuide.bad2" },
    { id: "bad3", captionKey: "docPhotoGuide.bad3" },
  ];

  return (
    <>
      {/* Bottom sheet */}
      <div
        className="fixed inset-0 z-50 flex items-end justify-center"
        style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
        onClick={onClose}
      >
        <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        <div
          className="w-full max-w-[480px] bg-white rounded-t-3xl px-5 pt-4 pb-8 overflow-y-auto overscroll-contain"
          style={{ animation: "slideUp 0.28s cubic-bezier(0.32,0.72,0,1)", maxHeight: "70vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header: handle + close button */}
          <div className="flex items-center justify-between mb-3">
            <div className="w-8" />
            <div className="w-10 h-1 bg-slate-200 rounded-full" />
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-500 cursor-pointer transition-colors"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>

          {/* Title */}
          <h2 className="text-[20px] font-bold text-slate-900 text-center mb-4 tracking-tight">
            {t("docPhotoGuide.title")}
          </h2>

          {/* Good example section title */}
          <div className="flex items-center gap-2 mb-2">
            <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
              <i className="ri-check-line text-emerald-600 text-xs" />
            </div>
            <span className="text-[13px] font-semibold text-emerald-700">{t("docPhotoGuide.goodTitle")}</span>
          </div>

          {/* Good example */}
          <div className="mb-5">
            <div className="relative">
              <div className="absolute top-2.5 left-2.5 z-10 flex items-center justify-center bg-emerald-500 text-white text-[13px] font-bold w-7 h-7 rounded-full">
                ✓
              </div>
              <div className="absolute bottom-2 right-2 z-10 w-6 h-6 rounded-full bg-white/80 flex items-center justify-center">
                <i className="ri-zoom-in-line text-emerald-600 text-xs" />
              </div>
              <button
                type="button"
                onClick={() => setExpandedImage("good")}
                className="w-full border-2 border-emerald-300 rounded-2xl overflow-hidden cursor-pointer active:opacity-80 transition-opacity block"
                style={{ aspectRatio: "4/3" }}
                aria-label="Good document example"
              >
                <GoodDocSVG />
              </button>
            </div>
            <p className="text-[15px] text-emerald-700 mt-1.5 leading-snug px-0.5">
              {(() => {
                const caption = t("docPhotoGuide.good");
                const dashIdx = caption.indexOf(" — ");
                if (dashIdx === -1) return caption;
                return <><strong>{caption.slice(0, dashIdx)}</strong>{caption.slice(dashIdx)}</>;
              })()}
            </p>
          </div>

          <div className="border-t border-slate-100 my-3" />

          {/* Bad examples section title */}
          <div className="flex items-center gap-2 mb-2 mt-2">
            <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-close-line text-red-600 text-xs" />
            </div>
            <span className="text-[13px] font-semibold text-red-700">{t("docPhotoGuide.badTitle")}</span>
          </div>

          {/* Bad examples carousel */}
          <div className="mb-5">
            <div className="relative">
              <div className="absolute top-2.5 left-2.5 z-10 flex items-center justify-center bg-red-500 text-white w-7 h-7 rounded-full">
                <i className="ri-close-line text-white text-base" />
              </div>
              <div className="absolute bottom-2 right-2 z-10 w-6 h-6 rounded-full bg-white/80 flex items-center justify-center">
                <i className="ri-zoom-in-line text-red-400 text-xs" />
              </div>
              <button
                type="button"
                onClick={() => setExpandedImage(badSlides[carouselIndex].id)}
                className="w-full border-2 border-red-300 rounded-2xl overflow-hidden cursor-pointer active:opacity-80 transition-opacity block"
                style={{ aspectRatio: "4/3" }}
                aria-label={`Bad document example ${carouselIndex + 1}`}
              >
                {carouselIndex === 0 && <ShadowDocSVG />}
                {carouselIndex === 1 && <AngledDocSVG />}
                {carouselIndex === 2 && <DarkDocSVG />}
              </button>
              <button
                type="button"
                onClick={() => setCarouselIndex((i) => (i - 1 + badSlides.length) % badSlides.length)}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 w-11 h-11 rounded-full bg-slate-800 border-2 border-white flex items-center justify-center cursor-pointer hover:bg-slate-700 active:scale-95 transition-all z-10"
              >
                <i className="ri-arrow-left-s-line text-white text-xl" />
              </button>
              <button
                type="button"
                onClick={() => setCarouselIndex((i) => (i + 1) % badSlides.length)}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 w-11 h-11 rounded-full bg-slate-800 border-2 border-white flex items-center justify-center cursor-pointer hover:bg-slate-700 active:scale-95 transition-all z-10"
              >
                <i className="ri-arrow-right-s-line text-white text-xl" />
              </button>
            </div>
            <p className="text-[15px] text-red-600 mt-1.5 leading-snug px-0.5">
              {(() => {
                const caption = t(badSlides[carouselIndex].captionKey);
                const dashIdx = caption.indexOf(" — ");
                if (dashIdx === -1) return caption;
                return <><strong>{caption.slice(0, dashIdx)}</strong>{caption.slice(dashIdx)}</>;
              })()}
            </p>
            <div className="flex items-center justify-center gap-2 mt-3">
              {badSlides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setCarouselIndex(i)}
                  className={`rounded-full transition-all cursor-pointer ${
                    i === carouselIndex ? "w-5 h-2 bg-red-400" : "w-2 h-2 bg-slate-300"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="w-full h-12 bg-slate-900 text-white rounded-xl text-[15px] font-semibold cursor-pointer hover:bg-slate-800 transition-colors whitespace-nowrap"
          >
            {t("section6.photoGotIt")}
          </button>
        </div>
      </div>

      {/* Fullscreen expand overlay */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setExpandedImage(null)}
        >
          <button
            type="button"
            onClick={() => setExpandedImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors z-10"
          >
            <i className="ri-close-line text-white text-xl" />
          </button>
          <div
            className="max-w-sm w-full flex flex-col items-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`w-full rounded-2xl overflow-hidden border-2 ${
                expandedImage === "good" ? "border-emerald-400" : "border-red-400"
              }`}
            >
              <FullscreenDocSVG id={expandedImage} />
            </div>
            <p className={`text-[13px] text-center leading-snug px-2 ${
              expandedImage === "good" ? "text-emerald-300" : "text-red-300"
            }`}>
              {expandedImage === "good"
                ? t("docPhotoGuide.good")
                : t(
                    expandedImage === "bad1" ? "docPhotoGuide.bad1" :
                    expandedImage === "bad2" ? "docPhotoGuide.bad2" :
                    "docPhotoGuide.bad3"
                  )
              }
            </p>
          </div>
        </div>
      )}
    </>
  );
}
